// Merch math for the free Expense Tracker sidebar. Pure functions so the numbers are testable.
// Hard merch = vinyl, CDs, posters. Soft merch = shirts, hats. "Band %" is the share the band keeps,
// so 80/20 means the band keeps 80 and the venue keeps 20.

export const MERCH_PRESETS = [
  { label: '80/20', band: 80, venue: 20 },
  { label: '85/15', band: 85, venue: 15 },
  { label: '90/10', band: 90, venue: 10 },
];

// Client default: soft goods 80/20, hard goods 90/10.
export const MERCH_DEFAULT_HARD_BAND_PCT = 90;
export const MERCH_DEFAULT_SOFT_BAND_PCT = 80;
export const MERCH_NO_BAND = 'No band set';

// Every field a merch entry writes. When an edited entry stops being merch these are cleared so
// old hard/soft numbers, band, and net do not linger on a fuel or crew entry.
export const MERCH_ENTRY_FIELDS = [
  'hardGross', 'softGross', 'hardBandPct', 'softBandPct', 'sellerFee', 'sellerFeeType',
  'sellerFeeNote', 'sellerFeeHours', 'productionCost', 'productionItems', 'band', 'merchNet',
];

const num = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

// Blank or unreadable falls back to the default; a typed 0 is a real 0. Always held to 0-100.
const pct = (v, fallback) => {
  const raw = String(v == null ? '' : v).trim();
  if (raw === '') return fallback;
  const x = parseFloat(raw);
  return Number.isFinite(x) ? Math.min(100, Math.max(0, x)) : fallback;
};

// One production-cost line: one-off amount, or # of units x price per unit.
export const productionLineTotal = (item) => {
  if (!item) return 0;
  return item.unitMode === 'units' ? num(item.unitCount) * num(item.unitPrice) : num(item.amount);
};

export const productionTotal = (items) => (Array.isArray(items) ? items : [])
  .reduce((sum, item) => sum + productionLineTotal(item), 0);

const trimNum = (n) => String(Math.round(n * 100) / 100);

export function merchPresetFor(bandPct) {
  const raw = String(bandPct == null ? '' : bandPct).trim();
  if (raw === '') return null;
  const x = parseFloat(raw);
  return MERCH_PRESETS.find((p) => p.band === x) || null;
}

export function merchSplitLabel(bandPct) {
  const band = pct(bandPct, 0);
  return `${trimNum(band)}/${trimNum(100 - band)}`;
}

/** Everything one merch entry adds up to. Accepts form strings or saved numbers. */
export function calcMerch(entry) {
  const e = entry || {};
  const hardGross = num(e.hardGross);
  const softGross = num(e.softGross);
  const gross = hardGross + softGross;
  const hardBandPct = pct(e.hardBandPct, MERCH_DEFAULT_HARD_BAND_PCT);
  const softBandPct = pct(e.softBandPct, MERCH_DEFAULT_SOFT_BAND_PCT);
  const hardBand = hardGross * hardBandPct / 100;
  const softBand = softGross * softBandPct / 100;
  const hardVenue = hardGross - hardBand;
  const softVenue = softGross - softBand;
  const feeInput = num(e.sellerFee);
  // Hourly is rate x hours. Older hourly entries had no hours, so they still count the rate once.
  const hours = num(e.sellerFeeHours);
  let fee = feeInput;
  if (e.sellerFeeType === 'pct') fee = gross * feeInput / 100;
  else if (e.sellerFeeType === 'hourly' && hours > 0) fee = feeInput * hours;
  const production = Array.isArray(e.productionItems) && e.productionItems.length
    ? productionTotal(e.productionItems)
    : num(e.productionCost);
  const net = Math.max(0, hardBand + softBand - fee);
  return {
    hardGross,
    softGross,
    gross,
    hardBandPct,
    softBandPct,
    hardBand,
    softBand,
    hardVenue,
    softVenue,
    venueCut: hardVenue + softVenue,
    fee,
    hours,
    production,
    net,
    profit: net - production,
  };
}

export const calcMerchNet = (entry) => calcMerch(entry).net;

export function isMerchEntry(entry) {
  return !!entry && entry.type === 'expense' && entry.category === 'merch';
}

function addInto(acc, m) {
  acc.hardGross += m.hardGross;
  acc.softGross += m.softGross;
  acc.gross += m.gross;
  acc.venueCut += m.venueCut;
  acc.fee += m.fee;
  acc.production += m.production;
  acc.net += m.net;
  acc.profit += m.profit;
  return acc;
}

const emptyTotals = () => ({
  hardGross: 0, softGross: 0, gross: 0, venueCut: 0, fee: 0, production: 0, net: 0, profit: 0,
});

/** Hard, soft, and entire totals across every merch entry in the list. */
export function merchSummary(entries) {
  const merch = (entries || []).filter(isMerchEntry);
  const out = merch.reduce((acc, e) => addInto(acc, calcMerch(e)), emptyTotals());
  out.count = merch.length;
  return out;
}

/** Merch totals grouped by the band name on each entry; unnamed entries land last. */
export function merchByBand(entries) {
  const groups = [];
  (entries || []).filter(isMerchEntry).forEach((e) => {
    const band = String(e.band || '').trim() || MERCH_NO_BAND;
    let g = groups.find((x) => x.band === band);
    if (!g) {
      g = { band, count: 0, ...emptyTotals() };
      groups.push(g);
    }
    g.count += 1;
    addInto(g, calcMerch(e));
  });
  groups.sort((a, b) => (a.band === MERCH_NO_BAND) - (b.band === MERCH_NO_BAND));
  return groups;
}
